import type { EndpointDto } from '@agent-platform/shared';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface ExpectedHeader {
  key: string;
  secret: boolean;
  required: boolean;
}

/** Headers the endpoint mapping reads, in rule order, deduplicated. */
export function expectedHeaders(endpoint: EndpointDto | undefined): ExpectedHeader[] {
  const out = new Map<string, ExpectedHeader>();
  for (const rule of endpoint?.mapping.rules ?? []) {
    if (rule.source.from !== 'header') continue;
    const key = rule.source.key.toLowerCase();
    const prev = out.get(key);
    out.set(key, {
      key,
      secret: (prev?.secret ?? false) || rule.secret,
      required: (prev?.required ?? false) || rule.transforms.some((t) => t.op === 'required'),
    });
  }
  return [...out.values()];
}

interface Row {
  key: string;
  value: string;
}
interface Stored {
  enabled: boolean;
  rows: Row[];
}

/**
 * Test headers for one agent's chat, kept in this browser tab only (they are often tokens):
 * sessionStorage survives a reload but not closing the tab.
 */
export function useTestHeaders(agentId: string, expected: ExpectedHeader[]) {
  const storageKey = `agent-test-headers:${agentId}`;
  const [state, setState] = useState<Stored | null>(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Stored) : null;
    } catch {
      return null;
    }
  });
  // Until the user edits anything, offer the headers the mapping expects.
  const expectedKeys = expected.map((e) => e.key).join(',');
  const value: Stored = useMemo(
    () =>
      state ?? {
        enabled: expected.length > 0,
        rows: expected.map((e) => ({ key: e.key, value: '' })),
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `expected` is compared by its keys
    [state, expectedKeys],
  );
  useEffect(() => {
    if (!state) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Storage unavailable: the values still apply for this page.
    }
  }, [state, storageKey]);

  const headers = value.enabled
    ? Object.fromEntries(value.rows.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]))
    : undefined;
  return { ...value, headers, set: (next: Stored) => setState(next) };
}

/** Key/value rows for the chat's test headers, plus the switch that applies the mapping. */
export function HeadersEditor({
  expected,
  enabled,
  rows,
  onChange,
}: {
  expected: ExpectedHeader[];
  enabled: boolean;
  rows: Row[];
  onChange: (next: { enabled: boolean; rows: Row[] }) => void;
}) {
  const info = (key: string) => expected.find((e) => e.key === key.trim().toLowerCase());
  const setRow = (i: number, patch: Partial<Row>) =>
    onChange({ enabled, rows: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className="grid gap-2.5">
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">Apply the endpoint mapping</span>
        <input
          type="checkbox"
          role="switch"
          className="accent-primary size-4"
          checked={enabled}
          onChange={(e) => onChange({ enabled: e.target.checked, rows })}
        />
      </label>
      <ul className={cn('grid gap-1.5', !enabled && 'opacity-50')}>
        {rows.map((row, i) => {
          const meta = info(row.key);
          return (
            <li key={i} className="grid grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_auto] gap-1.5">
              <Input
                value={row.key}
                placeholder="header-name"
                aria-label={`Header ${i + 1} name`}
                disabled={!enabled}
                className="h-7 font-mono text-xs"
                onChange={(e) => setRow(i, { key: e.target.value })}
              />
              <Input
                value={row.value}
                type={meta?.secret ? 'password' : 'text'}
                autoComplete="off"
                placeholder={meta?.required ? 'required' : 'value'}
                aria-label={`Header ${row.key || i + 1} value`}
                disabled={!enabled}
                className="h-7 font-mono text-xs"
                onChange={(e) => setRow(i, { value: e.target.value })}
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove header ${row.key || i + 1}`}
                disabled={!enabled}
                onClick={() => onChange({ enabled, rows: rows.filter((_, j) => j !== i) })}
              >
                <X aria-hidden />
              </Button>
            </li>
          );
        })}
      </ul>
      <Button
        variant="ghost"
        size="xs"
        className="w-fit"
        disabled={!enabled}
        onClick={() => onChange({ enabled, rows: [...rows, { key: '', value: '' }] })}
      >
        <Plus aria-hidden />
        Add header
      </Button>
      <p className="text-muted-foreground text-xs">
        Sent with every chat message and mapped exactly like a gateway call. Kept in this tab only;
        closing it clears them.
      </p>
    </div>
  );
}
